import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AlertCircle, ChevronRight, CircleStop, ClipboardList, Copy, Hammer, PlayCircle, Settings, Workflow as WorkflowIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import AppSidebar from '@/components/AppSidebar'
import EditableTitle from '@/components/EditableTitle'
import { IntuiBadge } from '@/components/intueri/IntuiBadge/IntuiBadge'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { cn } from '@/lib/utils'
import WorkflowCanvas from './WorkflowCanvas'
import {
  buildWorkflowGraph,
  buildWorkflowPlanGraph,
} from '../workflowGraph'
import {
  getWorkflowTemplateDefinition,
  renameWorkflowTemplate,
  saveWorkflow,
  upsertWorkflowDraft,
} from '../workflowApi'
import {
  frequencyLabel,
  getWorkflowSettings,
  hydrateWorkflowSettings,
  subscribeWorkflowSettings,
  updateWorkflowSettings,
} from '../workflowSettings'
import type {
  WorkflowGraphNodeData,
} from '../workflowTypes'
import { ToolCallDetailsDialog } from './TroubleshootingPanels'
import FloatingChatPanel from './FloatingChatPanel'
import NodePropertiesPanel from './NodePropertiesPanel'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import SelectFilesDialog from './SelectFilesDialog'
import SettingsPanel from './SettingsPanel'
import WorkflowReportView from '../report/WorkflowReportView'
import {
  TROUBLESHOOTING_TERMINAL_STATUSES,
  useTroubleshootingWorkflow,
} from './useTroubleshootingWorkflow'

type BuilderView = 'build' | 'report'

/**
 * Report → View Run: the query-param transition that loads a specific run in
 * the Build view — set `runId`, drop `view=report`, keep everything else
 * (`templateId` in particular, so Save still versions the same workflow).
 * Pure; exported for tests.
 */
export function viewRunSearchParams(
  current: URLSearchParams,
  runId: string
): URLSearchParams {
  const params = new URLSearchParams(current)
  params.set('runId', runId)
  params.delete('view')
  // `reportRunId` is a ONE-SHOT deep link ("open this run's report"), consumed the
  // moment the report opens. Left in the URL it outlived the navigation: leaving
  // report view kept it, so a later click on the Report TAB — which means "show me
  // the report", not "re-open that run" — silently landed back in an old run's
  // detail instead of the history list.
  params.delete('reportRunId')
  return params
}

/**
 * The segmented control's view switch. Opens the report's LIST — so it drops the
 * one-shot `reportRunId` deep link a previous "View Report" click may have left in
 * the URL, which would otherwise hijack the tab into an old run's detail. The chat
 * card's own navigation sets `reportRunId` explicitly and is unaffected.
 * Pure; exported for tests.
 */
export function setViewSearchParams(
  current: URLSearchParams,
  next: BuilderView
): URLSearchParams {
  const params = new URLSearchParams(current)
  if (next === 'report') params.set('view', 'report')
  else params.delete('view')
  params.delete('reportRunId')
  return params
}

/** Run statuses that light the canvas "AI is working" glow. */
const GLOWING_STATUSES = new Set(['queued', 'running'])
/** Run statuses that read as an execution error in the header pill. */
const ERROR_STATUSES = new Set(['failed', 'blocked', 'guardrail_stopped', 'budget_limited'])

/**
 * The workflow builder (Figma "3. Create Workflow" / "5. Run Workflow"):
 * breadcrumb header with Draft pill · Settings · Save as draft · Deploy, a
 * full-bleed canvas (Outline/Detail toggle, Run Workflow button, running glow)
 * with the floating workflow chat bottom-right, and docked right panels for
 * Settings and Node Properties. Once the workflow exists, the header gains the
 * Build | Report segmented control (report = run history + details).
 */
export default function WorkflowBuilder() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const troubleshooting = useTroubleshootingWorkflow({ active: true })

  const view: BuilderView = searchParams.get('view') === 'report' ? 'report' : 'build'
  const isTestLaunch = searchParams.get('test') === '1'

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [filesOpen, setFilesOpen] = useState(false)
  const [name, setName] = useState('')
  // Last-committed title, for the inline header rename: revert target on an
  // empty/failed edit, and the "did it actually change?" guard.
  const savedNameRef = useRef('')
  // Ignore out-of-order rename responses: only the LATEST attempt may toast or
  // revert, so a rapid A→B where A's call fails after B's succeeds can't roll
  // the name back to a stale value.
  const renameVersionRef = useRef(0)
  const [description, setDescription] = useState('')

  // Settings persistence key: the opened workflow, else the generated draft's
  // id, else a shared "new workflow" bucket until an identity exists.
  const settingsKey =
    troubleshooting.openedTemplateId ??
    troubleshooting.draft?.definition.id ??
    'workflow-new'

  // Warm the settings cache from the server for the open workflow so the
  // node inspector / settings panel render server truth (best-effort).
  useEffect(() => {
    void hydrateWorkflowSettings(settingsKey)
  }, [settingsKey])

  // Schedule state backs the header lifecycle pill (Live/Inactive) — kept in
  // sync with settings writes (Active toggle, hydrate) via the subscription.
  const [scheduleActive, setScheduleActive] = useState(
    () => getWorkflowSettings(settingsKey).active
  )
  // Bumped on every settings write for this workflow so canvas nodes re-read
  // their per-step HITL overrides (keeps the canvas checkbox and the node
  // inspector's toggle in lockstep — they share the hitlByStep row).
  const [settingsVersion, setSettingsVersion] = useState(0)
  useEffect(() => {
    setScheduleActive(getWorkflowSettings(settingsKey).active)
    return subscribeWorkflowSettings((changedId) => {
      if (changedId === settingsKey) {
        setScheduleActive(getWorkflowSettings(settingsKey).active)
        setSettingsVersion((v) => v + 1)
      }
    })
  }, [settingsKey])

  const troubleshootingGraph = useMemo(
    () =>
      troubleshooting.steps.length > 0
        ? buildWorkflowGraph(troubleshooting.steps, troubleshooting.planPreview)
        : buildWorkflowPlanGraph(troubleshooting.planPreview),
    // settingsVersion: the snake-density preference (Settings) changes the
    // layout the builders compute — re-lay on any settings write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [troubleshooting.planPreview, troubleshooting.steps, settingsVersion]
  )

  // UI-70: the canvas is the outline. The control-flow projection it used to
  // switch to was fetched only for that view, so the fetch goes with it.
  const displayedGraph = troubleshootingGraph

  // While the draft is being generated (no run yet) the canvas shows the
  // "Generating workflow…" overlay — grayed out, no nodes — until it's ready.
  const generatingDraft =
    troubleshooting.loading && !troubleshooting.run && !troubleshooting.starting

  // Mirror an already-named/loaded workflow into the header/save name, and
  // backfill a loaded run's inputs (problem, time window, scope) into the hook
  // so a re-run keeps its parameters. Two independent guards so each part syncs
  // once and never clobbers a later edit.
  const namedSyncRef = useRef<string | null>(null)
  const inputSyncRef = useRef<string | null>(null)
  useEffect(() => {
    const def = troubleshooting.draft?.definition
    const run = troubleshooting.run
    const namedId = def?.id ?? run?.id ?? null
    if (namedId && namedSyncRef.current !== namedId) {
      namedSyncRef.current = namedId
      const hydratedTitle = def?.title ?? run?.planPreview?.title ?? troubleshooting.planPreview?.title ?? ''
      setName(hydratedTitle)
      savedNameRef.current = hydratedTitle.trim()
      // Any rename still in flight belonged to the PREVIOUS workflow identity.
      // Reset the version alongside savedNameRef so a late success/failure for
      // the old workflow can't write THIS workflow's name (its version guard
      // `renameVersionRef.current === version` will no longer match).
      renameVersionRef.current = 0
      setDescription(def?.description ?? '')
    }

    if (run?.id && inputSyncRef.current !== run.id) {
      inputSyncRef.current = run.id
      const input = (run.input ?? {}) as {
        problem?: string
        /**
         * What v2 stores. `problem` is v1's key and a v2 run never writes it, so a v2 run
         * rehydrated by `problem` alone left the box on its placeholder — and a rerun then
         * fired with the placeholder question instead of the one that was asked, changing
         * what the gate matched and what it could bind. Reported from dev testing
         * 2026-08-19 as clarifications being asked again on a rerun.
         */
        question?: string
        timeWindow?: { start?: string; end?: string }
        scope?: { tags?: string[]; equipment?: string[]; documentIds?: string[]; boardId?: string }
      }
      const problem = input.problem ?? input.question
      if (problem) troubleshooting.setProblem(problem)
      if (input.timeWindow?.start) troubleshooting.setTimeWindowStart(input.timeWindow.start)
      if (input.timeWindow?.end) troubleshooting.setTimeWindowEnd(input.timeWindow.end)
      if (input.scope?.boardId) troubleshooting.setScopeBoardId(input.scope.boardId)
      if (input.scope?.tags?.length) troubleshooting.setScopeTags(input.scope.tags.join(', '))
      if (input.scope?.equipment?.length) troubleshooting.setScopeEquipment(input.scope.equipment.join(', '))
      if (input.scope?.documentIds?.length)
        troubleshooting.setScopeDocumentIds(input.scope.documentIds.join(', '))
    }
    // Setters are stable; the *SyncRef guards prevent re-sync loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [troubleshooting.draft, troubleshooting.run, troubleshooting.planPreview])

  // "Test Workflow" from the library (?test=1): start a manual run once the
  // builder is mounted on the template, then drop the flag from the URL.
  const testFiredRef = useRef(false)
  useEffect(() => {
    if (!isTestLaunch || testFiredRef.current || troubleshooting.run) return
    testFiredRef.current = true
    const params = new URLSearchParams(searchParams)
    params.delete('test')
    setSearchParams(params, { replace: true })
    void troubleshooting.startRun()
    // startRun/searchParams are stable enough for a fire-once effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestLaunch, troubleshooting.run])

  const run = troubleshooting.run
  const runGlowing = Boolean(run && GLOWING_STATUSES.has(run.status))
  const runErrored = Boolean(run && ERROR_STATUSES.has(run.status))
  // The audit's WHY (run.error.reason for blocked/guardrail stops, or the
  // failure message) — shown on the pill's tooltip so the stop is explained.
  const runErrorDetail = (() => {
    const err = run?.error as Record<string, unknown> | null | undefined
    if (!err) return null
    for (const key of ['reason', 'message'] as const) {
      const value = err[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
    return null
  })()
  const runErrorLabel = (() => {
    switch (run?.status) {
      case 'blocked':
        return t('workflow:builder.run_blocked', { defaultValue: 'Run Blocked' })
      case 'guardrail_stopped':
        return t('workflow:builder.run_guardrail', { defaultValue: 'Guardrail Stop' })
      case 'budget_limited':
        return t('workflow:builder.run_budget', { defaultValue: 'Budget Limit Reached' })
      default:
        return t('workflow:builder.run_error', { defaultValue: 'Error Executing Steps' })
    }
  })()
  const runTerminal = Boolean(run && TROUBLESHOOTING_TERMINAL_STATUSES.has(run.status))
  // Stop Workflow (Figma "11. Stop"): a loaded, non-terminal run can be
  // cancelled from the head pill or the node-panel footer. During the startup
  // blip (starting/runPending, no run row yet) there is nothing to cancel and
  // both surfaces fall back to the passive "Running…" state.
  const runCancellable = Boolean(run && !runTerminal)
  const [stoppingRun, setStoppingRun] = useState(false)
  // A mistaken click mid-run is destructive (the run can't be resumed), so
  // both Stop surfaces open a confirmation dialog first; only the dialog's
  // confirm action actually cancels.
  const [confirmStopOpen, setConfirmStopOpen] = useState(false)
  const requestStop = useCallback(() => setConfirmStopOpen(true), [])
  const cancelRun = troubleshooting.cancelRun
  const stopRun = useCallback(async () => {
    setStoppingRun(true)
    try {
      await cancelRun()
    } finally {
      setStoppingRun(false)
    }
  }, [cancelRun])
  const hasWorkflow =
    troubleshooting.steps.length > 0 ||
    Boolean(troubleshooting.planPreview) ||
    Boolean(troubleshooting.openedTemplateId)
  // A parked run whose clarification offers the document route: the one moment when an upload
  // could mean either "attach this knowledge" or "answer with this now".
  const methodAskPending =
    troubleshooting.run?.status === 'waiting_for_user' &&
    (troubleshooting.clarificationOptions ?? []).includes('Send a document')

  const showRunButton =
    view === 'build' && hasWorkflow && (!run || runTerminal) && !generatingDraft
  // The run is in flight: the pre-run startup blip, or ANY non-terminal run.
  // Putra (2026-07-15): both Run affordances — the first node's head pill AND
  // the node-panel footer button — must STAY VISIBLE through the whole run,
  // showing a "Running…" spinner rather than disappearing. Keying this off
  // runGlowing (queued/running only) wrongly hid it during clarification
  // (waiting_for_user) and HITL pauses (awaiting_approval / paused) — those are
  // still an active run, so use !runTerminal (every error/stopped status is
  // itself terminal, so terminal states fall through to the Run button / error
  // pill).
  // runPending covers the gap between startRun's navigate and the first
  // successful run load — starting has already flipped false there, and
  // without the bridge the pill "stops" for a beat (or for good when the
  // first load fails) even though the run is executing server-side.
  const runInFlight =
    view === 'build' &&
    (troubleshooting.starting ||
      troubleshooting.runPending ||
      Boolean(run && !runTerminal))
  // Draft until Deploy: a fresh build or a generated-but-unpublished draft.
  // Draft = a fresh build, or an opened workflow that has never published.
  // (Deriving from `draft` broke once hydration set it for EVERY opened
  // workflow — published/Live ones showed a Draft pill.) A run restore leaves
  // openedPublished null: having run at all means it was published.
  const isDraftState = troubleshooting.openedTemplateId
    ? troubleshooting.openedPublished === false
    : true

  // Attach the Run action to the FIRST node so the pill renders on its head in
  // flow coordinates (pans/zooms with the graph) instead of floating over the
  // canvas center — WorkflowStepNode renders it when data.onRun is set. Also
  // resolves every node's EFFECTIVE HITL from the persisted per-step override
  // (hitlByStep) — the card only DISPLAYS it (Putra "10. Update": cannot
  // uncheck on the card; the toggle lives in the node settings). Unchecking
  // there removes the row from the node; checking a node that had no default
  // HITL makes the row appear.
  const canvasGraph = useMemo(() => {
    // A generation in flight REPLACES whatever is on the canvas, so the previous
    // workflow must not still be sitting there: the user typed a new request and was
    // left studying a chart that was about to be thrown away (FS item 1 — "no step
    // chart is on screen before the final clarification answer"). The overlay's own
    // comment already promised "grayed out, no nodes"; the graph was passed anyway,
    // so a REGENERATE showed the old steps under the Generating card.
    if (generatingDraft) return { nodes: [], edges: [] }
    const hitlByStep = getWorkflowSettings(settingsKey).hitlByStep
    const nodes = displayedGraph.nodes.map((node, index) => {
      let data = node.data
      if (data && !data.hitlReadonly) {
        // Same key derivation as NodePropertiesPanel — both surfaces must
        // read the same hitlByStep row.
        const stepKey = data.step?.stepId ?? data.draftPhase?.id ?? `title:${data.title}`
        const effective = hitlByStep[stepKey] ?? data.hitl === true
        if (effective !== (data.hitl === true)) {
          data = { ...data, hitl: effective }
        }
      }
      if (index === 0) {
        // In flight → keep the pill, showing "Stop Workflow" once the run row
        // is loaded and cancellable (Figma "11. Stop"), or the passive
        // "Running…" during the startup blip; else, when a run may be
        // launched, wire the clickable Run action. (runInFlight wins so the
        // pill never flips back to a clickable button mid-run.)
        if (runInFlight) {
          data = runCancellable
            ? { ...data, running: true, onStop: requestStop, stopping: stoppingRun }
            : { ...data, running: true }
        } else if (showRunButton) {
          data = { ...data, onRun: () => void troubleshooting.startRun() }
        }
      }
      return data === node.data ? node : { ...node, data }
    })
    return { nodes, edges: displayedGraph.edges }
    // startRun/requestStop are stable hook callbacks; runInFlight/
    // showRunButton/runCancellable/stoppingRun key the pill state;
    // settingsVersion re-reads hitlByStep after every settings write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedGraph, generatingDraft, showRunButton, runInFlight, runCancellable, stoppingRun, requestStop, settingsKey, settingsVersion])

  const workflowTitle =
    name.trim() ||
    troubleshooting.planPreview?.title ||
    t('workflow:builder.untitled', { defaultValue: 'Untitled Workflow' })

  // A built-in template is the ONE read-only case (matching the boards top-bar
  // name — editable for a real board, plain text otherwise). It's the only
  // workflow that is BOTH in the published list (openedPublished === true) AND
  // not authored (draftIsAuthored === false ⟺ summary.builtin). Everything else
  // stays editable: authored workflows, unsaved/generated drafts, run restores,
  // and the pre-hydration window. (Keying off `!draftIsAuthored` alone made all
  // of those read-only — it defaults false and is never set when hydration is
  // skipped for a run restore, so the field looked uneditable.)
  const isBuiltinTemplate =
    troubleshooting.openedPublished === true && troubleshooting.draftIsAuthored === false
  const nameEditable = !isBuiltinTemplate

  // Persist an inline rename (board-parity <EditableTitle> — Pegah, workflow
  // feedback 2026-07-14 "workflow name not editable"). EditableTitle only fires
  // onSave for a non-empty, CHANGED value (it reverts empties + Escape itself),
  // so this just persists. An authored tenant template renames on the server; a
  // new/unsaved draft carries the name into the save/deploy call via `name`.
  const handleRenameSave = (nextRaw: string) => {
    const next = nextRaw.trim()
    if (!next || next === savedNameRef.current) return
    const prev = savedNameRef.current
    setName(next)
    savedNameRef.current = next
    // Persist iff editable-and-opened: any opened non-built-in (authored,
    // draft, run restore). A new/unsaved draft (no id) carries the name into
    // save/deploy; a built-in never persists. Aligned with `nameEditable`.
    const id = troubleshooting.openedTemplateId
    if (!id || isBuiltinTemplate) return
    const version = ++renameVersionRef.current
    void renameWorkflowTemplate(id, next)
      .then(() => {
        if (renameVersionRef.current === version) {
          toast.success(t('workflow:builder.renamed', { defaultValue: 'Workflow renamed' }))
        }
      })
      .catch(() => {
        // Stale failures (a newer rename already superseded this one) must not
        // revert — only the latest attempt owns the name.
        if (renameVersionRef.current === version) {
          savedNameRef.current = prev
          setName(prev)
          toast.error(t('workflow:builder.rename_failed', { defaultValue: 'Could not rename workflow' }))
        }
      })
  }

  const setView = (next: BuilderView) => {
    setSearchParams(setViewSearchParams(searchParams, next), { replace: true })
  }

  // Report → View Run: load that specific run into the Build canvas via `?runId`
  // (the hook keys off the query param) and drop `view=report` so the builder
  // shows the run's graph + chat. A run parked on a clarification lands ready to
  // answer/resume. Not `replace`: back returns to the report list.
  const viewRun = (targetRunId: string) => {
    setSearchParams(viewRunSearchParams(searchParams, targetRunId))
  }

  // A direct `?view=report` URL on a brand-new builder (no workflow identity)
  // would bypass the segmented control's gating — bounce back to Build.
  useEffect(() => {
    if (view !== 'report' || troubleshooting.openedTemplateId || run) return
    const params = new URLSearchParams(searchParams)
    params.delete('view')
    setSearchParams(params, { replace: true })
    // searchParams/setSearchParams are stable enough for this URL guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, troubleshooting.openedTemplateId, run])

  // Attach/detach files. The picker opens PRE-CHECKED with the current
  // attachments and returns the FULL resulting catalog selection, so this
  // SYNCS rather than only adds: bare-name uploads are preserved, the
  // catalog-backed set is replaced with the confirmed picks, and the run scope
  // adds newly-selected / drops de-selected ids while keeping any
  // non-attachment scope ids intact.
  const handleFilesSelected = (files: Array<{ name: string; documentId?: string }>) => {
    const settings = getWorkflowSettings(settingsKey)
    const prevCatalogIds = new Set(
      settings.knowledgeFiles.flatMap((e) =>
        typeof e === 'object' && e.documentId ? [e.documentId] : []
      )
    )
    // Non-catalog (bare-name) entries can't be represented in the picker — keep them.
    const bareNames = settings.knowledgeFiles.filter((e) => typeof e === 'string' || !e.documentId)
    const confirmed = files.flatMap((f) =>
      f.documentId ? [{ name: f.name, documentId: f.documentId }] : []
    )
    const confirmedIds = new Set(confirmed.map((f) => f.documentId))
    updateWorkflowSettings(settingsKey, { knowledgeFiles: [...bareNames, ...confirmed] })

    const removed = new Set([...prevCatalogIds].filter((id) => !confirmedIds.has(id)))
    const scope = troubleshooting.scopeDocumentIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((id) => !removed.has(id))
    for (const id of confirmedIds) if (!scope.includes(id)) scope.push(id)
    troubleshooting.setScopeDocumentIds(scope.join(', '))

    toast.success(
      t('workflow:files.attachments_updated', { defaultValue: 'Attachments updated' })
    )
  }

  // Catalog-backed attachments (name+id) → pre-checked in the Add Files picker
  // so it round-trips add/remove (board Add-files parity). Re-reads on every
  // settings write (settingsVersion bumps on any change).
  const attachedCatalogFiles = useMemo(
    () =>
      getWorkflowSettings(settingsKey).knowledgeFiles.flatMap((e) =>
        typeof e === 'object' && e.documentId ? [{ name: e.name, documentId: e.documentId }] : []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsKey, settingsVersion]
  )

  // Generate from Settings > Configure: the instructions are the generation
  // prompt AND the run's problem statement.
  const handleGenerateFromSettings = (instructions: string) => {
    troubleshooting.setProblem(instructions)
    void troubleshooting.generateDraft('workflow_chat', instructions)
  }

  const resolveDefinition = async () => {
    let sourceDef = troubleshooting.draft?.definition
    if (!sourceDef && troubleshooting.run) {
      try {
        sourceDef = await getWorkflowTemplateDefinition(troubleshooting.run.templateId)
      } catch {
        sourceDef = undefined
      }
    }
    if (!sourceDef && troubleshooting.openedTemplateId) {
      try {
        sourceDef = await getWorkflowTemplateDefinition(troubleshooting.openedTemplateId)
      } catch {
        sourceDef = undefined
      }
    }
    return sourceDef
  }

  // Save as draft: persist the definition as a DRAFT template row (it shows in
  // the library with the Draft pill; Deploy later publishes it).
  const handleSaveDraft = async () => {
    const sourceDef = await resolveDefinition()
    if (!sourceDef) {
      // Belt-and-braces behind the disabled button: say why nothing was saved
      // instead of silently leaving the builder.
      toast.error(
        t('workflow:builder.nothing_to_save', {
          defaultValue: 'Generate or configure a workflow before deploying.',
        })
      )
      return
    }
    try {
      // Upsert: overwrites the row generation auto-saved (same id@version) so
      // edits + a header rename land instead of 409ing against it.
      await upsertWorkflowDraft({
        ...sourceDef,
        title: name.trim() || sourceDef.title,
        ...(description.trim() ? { description: description.trim() } : {}),
      })
      toast.success(t('workflow:builder.draft_saved', { defaultValue: 'Draft saved' }))
      navigate('/workflow')
    } catch (err) {
      // The server forks a new draft version over a published collision, so
      // a 409 here is only a genuine write race — the workflow row itself is
      // durable. Treat as saved, but carry a header rename through.
      const status = (err as { response?: { status?: number } } | null)?.response?.status
      if (status === 409) {
        const title = name.trim()
        if (title && title !== sourceDef.title) {
          try {
            await renameWorkflowTemplate(sourceDef.id, title)
          } catch {
            // Best-effort: the workflow itself is safe either way.
          }
        }
        toast.success(t('workflow:builder.draft_saved', { defaultValue: 'Draft saved' }))
        navigate('/workflow')
        return
      }
      toast.error(
        t('workflow:builder.draft_save_failed', { defaultValue: 'Could not save draft' })
      )
    }
  }

  // Deploy: persist the workflow as a published version (runnable + listed).
  const handleDeploy = async () => {
    const sourceDef = await resolveDefinition()
    if (!sourceDef) {
      toast.error(
        t('workflow:builder.nothing_to_save', {
          defaultValue: 'Generate or configure a workflow before deploying.',
        })
      )
      return
    }
    const title = name.trim() || sourceDef.title
    // Identity for Deploy = ONLY the workflow you opened (?templateId). A fresh
    // build inherits nothing — the server mints a brand-new workflow.
    const currentId = troubleshooting.openedTemplateId ?? undefined
    try {
      const saved = await saveWorkflow({
        ...(currentId ? { templateId: currentId } : {}),
        title,
        definition: sourceDef,
      })
      // A fresh build configures its settings under the pre-deploy bucket
      // ('workflow-new' / the draft id) but the server mints the REAL template
      // id at deploy time — carry the settings over so the library's Active
      // toggle / frequency reflect what the user configured before deploying.
      const deployedId = saved?.templateId
      if (deployedId && deployedId !== settingsKey) {
        updateWorkflowSettings(deployedId, getWorkflowSettings(settingsKey))
      }
      // Deploy ≠ scheduled: the Active toggle stays a deliberate opt-in, so
      // point the user at it instead of silently starting daily runs.
      const every = frequencyLabel(
        getWorkflowSettings(deployedId ?? settingsKey).frequency
      ).toLowerCase()
      toast.success(
        t('workflow:builder.deployed_schedule_hint', {
          defaultValue: 'Deployed — turn on Active in the library to run it {{every}}',
          every,
        })
      )
      navigate('/workflow')
    } catch {
      toast.error(t('workflow:builder.save_failed', { defaultValue: 'Could not save workflow' }))
    }
  }

  // Node properties panel: opens on node click (tool chips still open the
  // tool-call dialog via selectGraphNode's routing).
  const selectedNode = troubleshooting.selectedGraphNode
  const firstStepId = troubleshooting.steps[0]?.id
  const firstPhaseId = troubleshooting.planPreview?.phases?.[0]?.id
  const selectedIsFirst = Boolean(
    (selectedNode?.step && selectedNode.step.id === firstStepId) ||
      (!selectedNode?.step && selectedNode?.draftPhase && selectedNode.draftPhase.id === firstPhaseId)
  )
  const rightPanel: 'node' | 'settings' | null = selectedNode
    ? 'node'
    : settingsOpen
      ? 'settings'
      : null

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top bar */}
        <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-border bg-background px-6 dark:bg-dark-card">
          {/* Left + right are equal-flex so the centered Build|Report control
              holds the exact midpoint in BOTH views — hiding the right-side
              actions on the Report tab no longer shifts the tabs (no flicker). */}
          <div className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={() => navigate('/workflow')}
              className="flex shrink-0 items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <WorkflowIcon className="size-4" />
              {t('nav:menu.workflow', { defaultValue: 'Workflow' })}
            </button>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            {/* Name: editable (click-to-edit) for authored workflows + new
                drafts, read-only text for built-in templates — plus a
                hover Copy button. Mirrors the boards top-bar name
                (BoardTopBarV2 → EditableTitle). */}
            {/* Bounded width so a long name truncates instead of shoving the
                Build|Report tabs / action buttons off-screen (min-w-0 lets it
                shrink; the clamp caps it on wide screens). */}
            <span className="group flex min-w-0 max-w-[clamp(120px,28vw,320px)] items-center gap-1">
              {nameEditable ? (
                <EditableTitle
                  title={workflowTitle}
                  onSave={handleRenameSave}
                  className="!text-sm !font-medium !max-w-full text-foreground truncate min-w-0"
                />
              ) : (
                <span
                  className="truncate px-1.5 py-0.5 text-sm font-medium text-foreground"
                  title={workflowTitle}
                >
                  {workflowTitle}
                </span>
              )}
              {workflowTitle && (
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard?.writeText(workflowTitle)
                    toast.success(
                      t('workflow:builder.name_copied', { defaultValue: 'Workflow name copied' })
                    )
                  }}
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-50 hover:!opacity-100"
                  title={t('workflow:builder.copy_name', { defaultValue: 'Copy workflow name' })}
                  aria-label={t('workflow:builder.copy_name', { defaultValue: 'Copy workflow name' })}
                >
                  <Copy className="size-4 text-muted-foreground" />
                </button>
              )}
            </span>
          </div>

          {/* Center: Build | Report (once the workflow exists) + error pill */}
          <div className="flex shrink-0 items-center gap-3">
            {(troubleshooting.openedTemplateId || run) && (
              <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/60 p-1 dark:bg-dark-muted/60">
                {(
                  [
                    ['build', Hammer, t('workflow:builder.tab_build', { defaultValue: 'Build' })],
                    ['report', ClipboardList, t('workflow:builder.tab_report', { defaultValue: 'Report' })],
                  ] as const
                ).map(([value, Icon, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setView(value)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium transition-colors',
                      view === value
                        ? 'bg-background text-foreground shadow-sm dark:bg-dark-card'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className="size-4" />
                    {label}
                  </button>
                ))}
              </div>
            )}
            {runErrored && view === 'build' && (
              <span
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 dark:border-red-900 dark:bg-red-950/50 dark:text-red-400"
                title={runErrorDetail ?? undefined}
              >
                <AlertCircle className="size-4" />
                {runErrorLabel}
              </span>
            )}
          </div>

          <div className="flex flex-1 items-center justify-end gap-2">
            {/* Lifecycle pill (Figma 8-edge-cases Draft/Live/Inactive):
                Draft = unpublished · Live = deployed + schedule on ·
                Inactive = deployed, schedule off. */}
            {isDraftState ? (
              <IntuiBadge variant="secondary">
                {t('workflow:row.draft', { defaultValue: 'Draft' })}
              </IntuiBadge>
            ) : scheduleActive ? (
              <IntuiBadge className="border-transparent bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">
                {t('workflow:builder.live', { defaultValue: 'Live' })}
              </IntuiBadge>
            ) : (
              <IntuiBadge variant="secondary">
                {t('workflow:builder.inactive', { defaultValue: 'Inactive' })}
              </IntuiBadge>
            )}
            {/* Figma "8. Report": with the Report tab active the header keeps
                only the lifecycle pill — no Settings / Save as draft / Deploy. */}
            {view === 'build' && (
              <>
                <IntuiButton
                  variant="outline"
                  size="lg"
                  leftIcon={<Settings />}
                  onClick={() => {
                    troubleshooting.selectGraphNode(null)
                    setSettingsOpen((v) => !v)
                  }}
                  aria-pressed={settingsOpen}
                >
                  {t('workflow:builder.settings', { defaultValue: 'Settings' })}
                </IntuiButton>
                <IntuiButton
                  variant="outline"
                  size="lg"
                  // Same gating as Deploy: with nothing resolvable to save, the
                  // button must not silently navigate away.
                  disabled={!hasWorkflow && !troubleshooting.draft}
                  onClick={() => void handleSaveDraft()}
                >
                  {t('workflow:builder.save_draft', { defaultValue: 'Save as draft' })}
                </IntuiButton>
                <IntuiButton
                  size="lg"
                  leftIcon={<PlayCircle />}
                  // Deployable = a workflow surface exists OR an (unloaded) draft
                  // was just generated — the draft may not be in hasWorkflow yet.
                  disabled={!hasWorkflow && !troubleshooting.draft}
                  onClick={() => void handleDeploy()}
                >
                  {t('workflow:builder.deploy', { defaultValue: 'Deploy' })}
                </IntuiButton>
              </>
            )}
          </div>
        </header>

        {/* Body */}
        {view === 'report' ? (
          <WorkflowReportView
            templateId={troubleshooting.run?.templateId ?? troubleshooting.templateId}
            initialRunId={searchParams.get('reportRunId') ?? undefined}
            onViewRun={viewRun}
          />
        ) : (
          <div className="flex min-h-0 flex-1">
            <div className="relative min-w-0 flex-1">
              {/* Stop run, exactly as designed (Figma 14804-134577): a solid pill at the
                  bottom CENTRE of the canvas, outside the flow transform so it does not
                  pan or zoom with the graph. That was FS item 5's complaint — the control
                  lived on the first node and shrank out of sight at real step counts.
                  Deliberately just the button: the design carries no run-state line and no
                  idle Run affordance here (the chat and the node pill own starting).
                  Icon is CircleStop and the label is builder.stop, the same glyph and
                  wording the node pill and the properties footer use — all three fire
                  requestStop, so they must not read as different actions
                  (FS 2026-09-09). */}
              {runInFlight && (
                <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
                  <button
                    type="button"
                    disabled={stoppingRun}
                    onClick={requestStop}
                    className="pointer-events-auto inline-flex items-center gap-2 rounded-xl bg-foreground px-4 py-2 text-sm font-medium text-background shadow-lg transition-colors hover:bg-foreground/90 disabled:opacity-60"
                  >
                    <CircleStop className="size-3.5" aria-hidden />
                    {stoppingRun
                      ? t('workflow:builder.stopping_run', { defaultValue: 'Stopping…' })
                      : t('workflow:builder.stop', { defaultValue: 'Stop Workflow' })}
                  </button>
                </div>
              )}
              <WorkflowCanvas
                nodes={canvasGraph.nodes}
                edges={canvasGraph.edges}
                runningGlow={runGlowing}
                generatingLabel={
                  generatingDraft
                    ? t('workflow:builder.generating_draft', { defaultValue: 'Generating' })
                    : undefined
                }
                generatingBodyLabel={
                  generatingDraft
                    ? t('workflow:builder.generating_workflow', {
                        defaultValue: 'Generating Workflow',
                      })
                    : undefined
                }
                emptyState={
                  <div className="rounded-xl border border-border bg-background px-10 py-6 text-center shadow-sm dark:bg-dark-card">
                    <p className="text-sm font-semibold text-foreground">
                      {t('workflow:builder.empty_title', { defaultValue: "Let's build your workflow" })}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('workflow:builder.empty_hint', {
                        defaultValue: 'Use chat to describe your workflow',
                      })}
                    </p>
                  </div>
                }
                onNodeClick={(data) =>
                  troubleshooting.selectGraphNode(data as WorkflowGraphNodeData)
                }
              />

              {/* UI-70 (Jenna): "as a user i dont have any actionability from
                  the detail page and this is like seeing the agent's system
                  prompt, its internal workings that the user doesnt need to
                  know." The Outline | Detail toggle is gone, and with it the
                  Detail view it switched to — the backend's validated
                  control-flow DAG, which the canvas rendered with onNodeClick
                  deliberately undefined. A read-only projection of decision,
                  fanout and retry edges is engine internals; nothing on it
                  could be acted on, which is exactly the report.

                  The canvas is now always the outline — the plan/run cards,
                  the half a user can click. The projection itself is not
                  deleted: getWorkflowTemplateGraph and buildControlFlowGraph
                  keep their own tests, so putting a debug surface back is
                  re-adding a caller. */}

              {/* Floating workflow chat */}
              <FloatingChatPanel
                workflow={troubleshooting}
                title={name.trim() || troubleshooting.planPreview?.title}
                // A docked right panel owns that side of the screen — hide the
                // chat while one is open; it returns in the same state after.
                hidden={Boolean(rightPanel)}
                onAttachFile={() => setFilesOpen(true)}
                onOpenTemplate={(templateId) =>
                  navigate(`/workflow/new?templateId=${encodeURIComponent(templateId)}`)
                }
              />
            </div>

            {/* Docked right panel: node properties (priority) or settings */}
            {rightPanel && (
              <aside className="flex w-[400px] shrink-0 flex-col border-l border-border bg-background dark:bg-dark-card">
                {rightPanel === 'node' && selectedNode ? (
                  <NodePropertiesPanel
                    node={selectedNode}
                    workflowId={settingsKey}
                    isFirst={selectedIsFirst}
                    onClose={() => troubleshooting.selectGraphNode(null)}
                    // The Run / "Running…" affordance belongs to the ENTRY node
                    // only — opening any other node's details must not show it
                    // (it mirrors the first node's head pill). Non-first panels
                    // get neither onRun nor running.
                    onRun={
                      selectedIsFirst && showRunButton
                        ? () => void troubleshooting.startRun()
                        : undefined
                    }
                    running={selectedIsFirst && runInFlight}
                    onStop={selectedIsFirst && runCancellable ? requestStop : undefined}
                    stopping={stoppingRun}
                  />
                ) : (
                  <SettingsPanel
                    workflowId={settingsKey}
                    generating={troubleshooting.loading}
                    onClose={() => setSettingsOpen(false)}
                    onGenerate={handleGenerateFromSettings}
                    onUploadFiles={() => {
                      setFilesOpen(true)
                    }}
                  />
                )}
              </aside>
            )}
          </div>
        )}
      </div>

      {/* Stop Workflow confirmation (Figma "11. Stop"): a mistaken click on
          either Stop surface must not kill a live run — cancellation is not
          resumable, so it goes through this explicit confirm. */}
      <AlertDialog open={confirmStopOpen} onOpenChange={setConfirmStopOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('workflow:builder.stop_confirm_title', {
                defaultValue: 'Stop this workflow run?',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('workflow:builder.stop_confirm_description', {
                defaultValue:
                  'The run will stop after the current step finishes and cannot be resumed. You can start a new run at any time.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('workflow:builder.stop_confirm_keep', { defaultValue: 'Keep running' })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // The run may have reached a terminal state while the dialog
                // was open — cancelling then would 403; quietly no-op instead.
                if (runCancellable) void stopRun()
              }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {t('workflow:builder.stop', { defaultValue: 'Stop Workflow' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SelectFilesDialog
        open={filesOpen}
        onClose={() => setFilesOpen(false)}
        onSelect={handleFilesSelected}
        preselectedFiles={attachedCatalogFiles}
        // Only while the run is actually asking for a method. Reaching Add Files at that moment
        // is the natural gesture ("I need to give it this document") and it used to land on the
        // catalog pipeline — minutes of processing for a document the run needs now. The handler
        // is the intake card's own, so both doors lead to one implementation.
        onUseAsMethod={methodAskPending ? troubleshooting.supplyMethodDocument : undefined}
      />
      {troubleshooting.selectedToolCall && (
        <ToolCallDetailsDialog
          toolCall={troubleshooting.selectedToolCall}
          onClose={() => troubleshooting.setSelectedToolCall(null)}
        />
      )}
    </div>
  )
}
